// React hook for automatic price refresh with countdown timer
import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getMexcApiClient } from '../services/mexcApi';

export interface PriceRefreshState {
  isRefreshing: boolean;
  timeUntilRefresh: number; // seconds remaining
  lastRefreshTime: number | null;
  refreshCount: number;
  error: string | null;
}

export interface PriceRefreshControls {
  manualRefresh: () => Promise<void>;
  resetTimer: () => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  isPaused: boolean;
}

export interface UsePriceRefreshReturn {
  state: PriceRefreshState;
  controls: PriceRefreshControls;
  formatTimeRemaining: (seconds: number) => string;
}

const DEFAULT_REFRESH_INTERVAL = 1 * 60 * 1000; // 1 minute in milliseconds
const COUNTDOWN_UPDATE_INTERVAL = 1000; // Update countdown every second

export function usePriceRefresh(): UsePriceRefreshReturn {
  const queryClient = useQueryClient();
  const mexcApi = getMexcApiClient();

  // Get refresh interval from environment or use default
  const refreshInterval = parseInt(import.meta.env.VITE_PRICE_REFRESH_INTERVAL) || DEFAULT_REFRESH_INTERVAL;

  const [state, setState] = useState<PriceRefreshState>({
    isRefreshing: false,
    timeUntilRefresh: Math.floor(refreshInterval / 1000),
    lastRefreshTime: null,
    refreshCount: 0,
    error: null,
  });

  const [isPaused, setIsPaused] = useState(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  // Format time remaining as MM:SS
  const formatTimeRemaining = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // Perform price refresh by invalidating relevant queries
  const performRefresh = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isRefreshing: true, error: null }));

    try {
      console.log('🔄 Starting price refresh...');

      // First, get current positions to know which symbols to refresh
      const positions = queryClient.getQueryData(['positions']) as any[];
      const activeSymbols = positions ? [...new Set(positions.map(p => p.symbol))] : [];

      console.log('Active symbols for price refresh:', activeSymbols);

      // Invalidate all price-related queries to force fresh data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ticker'] }),
        queryClient.invalidateQueries({ queryKey: ['positions'] }),
        queryClient.invalidateQueries({ queryKey: ['openOrders'] }),
        queryClient.invalidateQueries({ queryKey: ['assets'] }),
      ]);

      // Fetch fresh ticker data for all active symbols AND store in cache
      if (activeSymbols.length > 0) {
        console.log('Fetching fresh ticker data for symbols:', activeSymbols);

        const tickerPromises = activeSymbols.map(async (symbol) => {
          try {
            const ticker = await mexcApi.getTicker(symbol);
            console.log(`✅ Fetched ticker for ${symbol}:`, ticker);

            // Store individual ticker data in React Query cache
            queryClient.setQueryData(['ticker', symbol], ticker);

            return { symbol, ticker };
          } catch (error) {
            console.error(`❌ Failed to fetch ticker for ${symbol}:`, error);
            return { symbol, ticker: null };
          }
        });

        await Promise.all(tickerPromises);

        // Also fetch all tickers to ensure we have comprehensive data
        try {
          const allTickers = await mexcApi.getTicker(); // Get all tickers
          console.log('✅ Fetched all tickers:', Array.isArray(allTickers) ? allTickers.length : 'single ticker');

          // Store all tickers data
          queryClient.setQueryData(['ticker'], allTickers);
        } catch (error) {
          console.error('❌ Failed to fetch all tickers:', error);
        }
      }

      setState(prev => ({
        ...prev,
        isRefreshing: false,
        lastRefreshTime: Date.now(),
        refreshCount: prev.refreshCount + 1,
        timeUntilRefresh: Math.floor(refreshInterval / 1000),
      }));

      // Reset the timer
      startTimeRef.current = Date.now();

      console.log('✅ Price refresh completed successfully');

    } catch (error) {
      console.error('❌ Price refresh failed:', error);
      setState(prev => ({
        ...prev,
        isRefreshing: false,
        error: error instanceof Error ? error.message : 'Refresh failed',
      }));
    }
  }, [queryClient, mexcApi, refreshInterval]);

  // Manual refresh function
  const manualRefresh = useCallback(async (): Promise<void> => {
    if (state.isRefreshing) return;

    // Clear existing timers
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    await performRefresh();

    // Restart the automatic refresh cycle
    if (!isPaused) {
      refreshTimeoutRef.current = setTimeout(performRefresh, refreshInterval);
    }
  }, [state.isRefreshing, performRefresh, refreshInterval, isPaused]);

  // Reset timer to full interval
  const resetTimer = useCallback(() => {
    setState(prev => ({
      ...prev,
      timeUntilRefresh: Math.floor(refreshInterval / 1000),
    }));
    startTimeRef.current = Date.now();

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (!isPaused) {
      refreshTimeoutRef.current = setTimeout(performRefresh, refreshInterval);
    }
  }, [refreshInterval, performRefresh, isPaused]);

  // Pause the timer
  const pauseTimer = useCallback(() => {
    setIsPaused(true);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  // Resume the timer
  const resumeTimer = useCallback(() => {
    setIsPaused(false);
    if (!refreshTimeoutRef.current) {
      const remainingTime = state.timeUntilRefresh * 1000;
      refreshTimeoutRef.current = setTimeout(performRefresh, remainingTime);
    }
  }, [state.timeUntilRefresh, performRefresh]);

  // Update countdown every second
  useEffect(() => {
    if (isPaused) return;

    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, Math.floor((refreshInterval - elapsed) / 1000));

      setState(prev => ({
        ...prev,
        timeUntilRefresh: remaining,
      }));

      // If countdown reaches zero, it means the refresh should have happened
      if (remaining === 0) {
        startTimeRef.current = Date.now();
      }
    }, COUNTDOWN_UPDATE_INTERVAL);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [refreshInterval, isPaused]);

  // Set up automatic refresh cycle
  useEffect(() => {
    if (isPaused) return;

    // Initial refresh after component mounts
    const initialDelay = 2000; // 2 seconds delay to let other components load
    const initialTimeout = setTimeout(performRefresh, initialDelay);

    // Set up recurring refresh
    refreshTimeoutRef.current = setTimeout(performRefresh, refreshInterval);

    return () => {
      clearTimeout(initialTimeout);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [performRefresh, refreshInterval, isPaused]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  return {
    state,
    controls: {
      manualRefresh,
      resetTimer,
      pauseTimer,
      resumeTimer,
      isPaused,
    },
    formatTimeRemaining,
  };
}
